import type { ReactNode } from 'react';
import React, { Component } from 'react';
import { ErrorState } from '../ErrorState';
import { addError } from '@shared/utils/logger';

interface ErrorBoundaryProps
  extends Readonly<{
    children: ReactNode;
  }> {}

type ErrorBoundaryState = Readonly<
  | {
      hasError: false;
    }
  | {
      hasError: true;
      error: Error;
    }
>;

/**
 * Error Boundary for Accommodations Console.
 * Catches JavaScript errors during rendering and shows ErrorState component.
 * Prevents the entire console from crashing due to unexpected render errors.
 */
export class AccommodationsConsoleErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Update state to trigger error UI
    this.setState({ hasError: true, error });

    // Log error to monitoring service
    addError(error, {
      context: 'AccommodationsConsoleErrorBoundary',
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorState />;
    }

    return this.props.children;
  }
}
